import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  api,
  type HandoffTargetTeamWireItem,
  type HandoffTargetWireItem,
  type HandoffWireItem,
} from '../lib/api-client';

/**
 * useHandoffs — Fatia G (ADR-049 + Caderno cap 39).
 *
 * Lista handoffs pendentes (bandeja) + mutations para iniciar/aceitar/
 * rejeitar/cancelar. Refresh em WS event `peer-coordination.handoff.*`.
 */
export function useHandoffs(params: {
  status?: 'pending' | 'rejected' | 'cancelled' | 'completed';
  forUser?: string;
  forQueue?: string;
}): {
  data: HandoffWireItem[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const query = useQuery({
    queryKey: ['handoffs', params],
    queryFn: async () => {
      const res = await api.listHandoffs(params);
      return res.data;
    },
    staleTime: 15 * 1000,
  });
  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => void query.refetch(),
  };
}

/**
 * useHandoffTargets — atendentes do tenant que podem receber um handoff push
 * (Item 293). Consome `GET /v1/handoffs/targets` (gated `peer-coordination-handoff`,
 * acessível ao atendente). Exclui o próprio usuário no backend.
 */
export function useHandoffTargets(): {
  data: HandoffTargetWireItem[];
  /** Times ativos como destino (A-32, 04/Set/2026) — vazio em engine anterior. */
  teams: HandoffTargetTeamWireItem[];
  isLoading: boolean;
} {
  const query = useQuery({
    queryKey: ['handoff-targets'],
    queryFn: async () => {
      const res = await api.listHandoffTargets();
      return { data: res.data, teams: res.teams ?? [] };
    },
    staleTime: 60 * 1000,
  });
  return {
    data: query.data?.data ?? [],
    teams: query.data?.teams ?? [],
    isLoading: query.isLoading,
  };
}

export function useInitiateHandoff(): ReturnType<
  typeof useMutation<{ handoffId: string }, Error, Parameters<typeof api.initiateHandoff>[0]>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof api.initiateHandoff>[0]) => api.initiateHandoff(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['handoffs'] });
    },
  });
}

export function useAcceptHandoff(): ReturnType<
  typeof useMutation<{ version: number }, Error, { id: string; version: number }>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) =>
      api.acceptHandoff(id, version),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['handoffs'] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

export function useRejectHandoff(): ReturnType<
  typeof useMutation<{ version: number }, Error, { id: string; version: number; reason: string }>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version, reason }: { id: string; version: number; reason: string }) =>
      api.rejectHandoff(id, version, reason),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['handoffs'] });
    },
  });
}

export function useCancelHandoff(): ReturnType<
  typeof useMutation<{ version: number }, Error, { id: string; version: number; reason?: string }>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version, reason }: { id: string; version: number; reason?: string }) =>
      api.cancelHandoff(id, version, reason),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['handoffs'] });
    },
  });
}
